import React, { useState, useCallback, ReactNode, useEffect } from "react";
import { Checkbox } from "@emisgroup/ui-checkbox";
import { IPractice } from "../Types";
import ChevronDown from "~icons/ic/outline-expand-more";
import ChevronRight from "~icons/ic/outline-chevron-right";
import "./style.scss";

export type ExpandedRow = {
  id: string; name: string | null; included?: string; excluded?: string; population?: string;
};
export type RowValue = string | boolean | ReactNode | ExpandedRow[] | IPractice[];

export interface Column<T extends RowData = RowData> {
  key: keyof T & string;
  header: string;
  width?: string;
  render?: (value: T[keyof T], row: T) => React.ReactNode;
}

export interface RowData {
  id: string;
  expandedKey?: string;
  [key: string]: RowValue;
}

interface IMultiSiteTableProps<T extends RowData = RowData> {
  columns: Column<T>[];
  rowData: T[];
  isExpandable?: (row: T) => boolean;
  onExpandRow?: (row: T) => T[];
  isSelection?: boolean;
  onSelectionChange?: (selectedRows: T[]) => void;
  selectedRows?: (string)[];
  onRowClicked?: (row: T) => void;
}

export const MultiSiteTable = <T extends RowData = RowData>({
  columns,
  rowData,
  isExpandable = (): boolean => false,
  onExpandRow,
  isSelection = false,
  onSelectionChange,
  selectedRows = [],
  onRowClicked
}: IMultiSiteTableProps<T>): React.ReactElement => {
  const [expandedRows, setExpandedRows] = useState<Record<string, T[]>>({});
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set(selectedRows));
  const getRowKey = (row: T): string => row.id ?? row.expandedKey ?? "";

  const handleRowExpand = useCallback((row: T) => {
    if (!onExpandRow) return;
    const rowKey = getRowKey(row);

    if (expandedRows[rowKey]) {
      setExpandedRows(prev => {
        const newExpanded = { ...prev };
        delete newExpanded[rowKey];
        return newExpanded;
      });
      return;
    }

    const expandedData = onExpandRow(row);
    setExpandedRows(prev => ({ ...prev, [rowKey]: expandedData }));
  }, [onExpandRow, expandedRows]);

  const handleRowClick = useCallback((row: T, event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest(".checkbox-wrapper") || target.closest(".expand-column")) {
      return;
    }
    if (onRowClicked) {
      onRowClicked(row);
    }
  }, [onRowClicked]);

  useEffect(() => {
    setSelectedRowIds(new Set(selectedRows));
  }, [selectedRows]);

  const handleRowSelection = useCallback((rowId: string, isSelected: boolean) => {
    const newSelectedRows = new Set<string>(selectedRowIds);

    if (isSelected) {
      newSelectedRows.add(rowId);
    } else {
      newSelectedRows.delete(rowId);
    }

    setSelectedRowIds(newSelectedRows);

    if (onSelectionChange) {
      const selectedRowsData = rowData.filter(row => newSelectedRows.has(getRowKey(row)));
      onSelectionChange(selectedRowsData);
    }
  }, [selectedRowIds, rowData, onSelectionChange]);

  const handleSelectAll = useCallback((isSelected: boolean) => {
    const newSelectedRows =
      isSelected ? new Set<string>(rowData.map(row => getRowKey(row))) : new Set<string>();
    setSelectedRowIds(newSelectedRows);

    if (onSelectionChange) {
      const selectedRowsData = isSelected ? [...rowData] : [];
      onSelectionChange(selectedRowsData);
    }
  }, [rowData, onSelectionChange]);

  const isAllSelected = selectedRowIds.size === rowData.length && rowData.length > 0;
  const isIndeterminate = selectedRowIds.size > 0 && selectedRowIds.size < rowData.length;

  const renderCell = (column: Column<T>, row: T): React.ReactNode => {
    if (column.render) {
      return column.render(row[column.key], row);
    }
    return row[column.key] as React.ReactNode;
  };

  const renderExpandButton = (row: T): React.JSX.Element | null => {
    const canExpand = isExpandable(row);
    const isExpanded = !!expandedRows[getRowKey(row)];

    if (!canExpand) {
      return null;
    }

    return (
      <div
        onClick={(): void => { handleRowExpand(row); }}
        aria-label={isExpanded ? "Collapse row" : "Expand row"}
        style={{ cursor: "pointer" }}
      >
        {isExpanded ? (
          <ChevronDown title={""}/>
        ) : (
          <ChevronRight title={""}/>
        )}
      </div>
    );
  };

  const renderExpandedRows = (parentRow: T): React.JSX.Element[] | null => {
    const expandedKey = getRowKey(parentRow);
    const expanded = expandedRows[expandedKey];
    if (!expanded || expanded.length === 0) return null;

    return expanded.map((expandedRow, index) => {
      const _row = expandedRow as unknown as ExpandedRow;

      return (
        <tr key={`${expandedKey}-expanded-${index}`}>
          {isSelection && <td></td>}
          <td className='multi-site-table-cell'></td>
          <td className='multi-site-table-cell'>{_row.name}</td>
          <td className='multi-site-table-cell'>-</td>
          <td className='multi-site-table-cell'>-</td>
          <td className='multi-site-table-cell'>-</td>
          <td className='multi-site-table-cell'>{_row.included}</td>
          <td className='multi-site-table-cell'>{_row.excluded}</td>
          <td className='multi-site-table-cell'>{_row.population}</td>
        </tr>
      );
    });
  };

  return (
    <table className="multi-site-table">
      <thead>
        <tr style={{ backgroundColor: "#f8f9fa" }}>
          {isSelection && (
            <th style={{ width: "50px" }}>
              <Checkbox
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={handleSelectAll}
                aria-label="Select all rows"
              />
            </th>
          )}
          <th className="expand-column">
            {""}
          </th>
          {columns.map((column) => (
            <th
              key={column.key}
              style={{
                width: column.width
              }}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rowData.map((row) => {
          const rowKey = getRowKey(row);
          const isSelected = selectedRowIds.has(rowKey);
          const isExpanded = !!expandedRows[rowKey];

          return (
            <React.Fragment key={row.id}>
              <tr
                onClick={(event): void => handleRowClick(row, event)}
                style={{
                  backgroundColor: isSelected ? "#e3f2fd" : "white",
                  borderBottom: "1px solid #dee2e6",
                  cursor: onRowClicked ? "pointer" : "default"
                }}
              >
                {isSelection && (
                  <td className="multi-site-table-cell checkbox-wrapper" style={{ textAlign: "center" }}>
                    <Checkbox
                      checked={isSelected}
                      onChange={(checked): void => handleRowSelection(rowKey, checked)}
                      aria-label={`Select row ${rowKey}`}
                    />
                  </td>
                )}
                <td className="multi-site-table-cell expand-column">
                  {renderExpandButton(row)}
                </td>
                {columns.map((column) => {
                  const width = column.width ? {
                    width: column.width,
                    minWidth: column.width,
                    maxWidth: column.width
                  } : {};

                  return (
                    <td
                      key={column.key}
                      className="multi-site-table-cell"
                      style={width}
                    >
                      {renderCell(column, row)}
                    </td>
                  );
                })}
              </tr>
              {isExpanded && renderExpandedRows(row)}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};
